/**
 * Railway API Debugging Utilities
 * Logs API calls and errors for debugging Railway connectivity issues.
 * Only logs when EXPO_PUBLIC_DEBUG_API=true|1 (opt-in).
 * Updated to handle both fetch Response and axios AxiosResponse/AxiosError
 */

import axios, { AxiosResponse, AxiosError } from 'axios'
import { isApiDebugEnabled } from '../constants/debug'
import { API_URL, ENV_INFO } from '../constants/api'

function redactAuthHeaders(headers: unknown): unknown {
  if (!headers || typeof headers !== 'object') return headers
  const out = { ...(headers as Record<string, unknown>) }
  const authKey = Object.keys(out).find((k) => k.toLowerCase() === 'authorization')
  if (authKey && typeof out[authKey] === 'string' && (out[authKey] as string).startsWith('Bearer ')) {
    out[authKey] = 'Bearer ***REDACTED***'
  }
  return out
}

/**
 * Log Railway API debug information
 * Only logs when EXPO_PUBLIC_DEBUG_API=true|1
 */
export const logRailwayDebugInfo = () => {
  if (!isApiDebugEnabled) return
  // eslint-disable-next-line no-console
  console.group('🔍 Railway API Debug Info')
  // eslint-disable-next-line no-console
  console.log('API URL:', API_URL)
  // eslint-disable-next-line no-console
  console.log('Environment:', ENV_INFO.useProduction ? 'production' : 'development')
  // eslint-disable-next-line no-console
  console.log('Full ENV Info:', ENV_INFO)
  // eslint-disable-next-line no-console
  console.log('NODE_ENV:', process.env.NODE_ENV)
  // eslint-disable-next-line no-console
  console.log('EXPO_PUBLIC_ENV:', process.env.EXPO_PUBLIC_ENV)
  // eslint-disable-next-line no-console
  console.log('Timestamp:', new Date().toISOString())
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/**
 * Log API request details
 * Only logs when EXPO_PUBLIC_DEBUG_API=true|1
 * Supports both fetch RequestInit and axios AxiosRequestConfig
 */
export const logApiRequest = (
  endpoint: string,
  options?: { method?: string; headers?: unknown; body?: unknown; data?: unknown }
) => {
  if (!isApiDebugEnabled) return
  // eslint-disable-next-line no-console
  console.group(`📤 API Request: ${endpoint}`)
  // eslint-disable-next-line no-console
  console.log('Full URL:', `${API_URL}${endpoint}`)
  // eslint-disable-next-line no-console
  console.log('Method:', options?.method || 'GET')
  // eslint-disable-next-line no-console
  console.log('Headers:', redactAuthHeaders(options?.headers))
  // eslint-disable-next-line no-console
  console.log('Body/Data:', options?.body || options?.data)
  // eslint-disable-next-line no-console
  console.log('Timestamp:', new Date().toISOString())
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/**
 * Log API response details
 * Only logs when EXPO_PUBLIC_DEBUG_API=true|1
 * Supports both fetch Response and axios AxiosResponse
 */
export const logApiResponse = (
  endpoint: string,
  response: Response | AxiosResponse,
  data?: unknown
) => {
  if (!isApiDebugEnabled) return
  // eslint-disable-next-line no-console
  console.group(`📥 API Response: ${endpoint}`)

  // Handle both fetch Response and axios AxiosResponse
  if ('status' in response && 'statusText' in response) {
    const axiosResponse = response as AxiosResponse
    // eslint-disable-next-line no-console
    console.log('Status:', axiosResponse.status, axiosResponse.statusText)
    // eslint-disable-next-line no-console
    console.log('OK:', axiosResponse.status >= 200 && axiosResponse.status < 300)
    // eslint-disable-next-line no-console
    console.log('Headers:', axiosResponse.headers)
    if (data !== undefined) {
      // eslint-disable-next-line no-console
      console.log('Data:', data)
    } else {
      // eslint-disable-next-line no-console
      console.log('Data:', axiosResponse.data)
    }
  } else {
    const fetchResponse = response as Response
    // eslint-disable-next-line no-console
    console.log('Status:', fetchResponse.status, fetchResponse.statusText)
    // eslint-disable-next-line no-console
    console.log('OK:', fetchResponse.ok)
    // eslint-disable-next-line no-console
    console.log('Headers:', Object.fromEntries(fetchResponse.headers.entries()))
    if (data) {
      // eslint-disable-next-line no-console
      console.log('Data:', data)
    }
  }

  // eslint-disable-next-line no-console
  console.log('Timestamp:', new Date().toISOString())
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/**
 * Log API error
 * Only logs when EXPO_PUBLIC_DEBUG_API=true|1
 * Handles both standard Error and AxiosError
 */
export const logApiError = (endpoint: string, error: Error | AxiosError | unknown, context?: string) => {
  if (!isApiDebugEnabled) return
  // eslint-disable-next-line no-console
  console.group(`❌ API Error: ${endpoint}`)
  if (context) {
    // eslint-disable-next-line no-console
    console.log('Context:', context)
  }

  // Handle AxiosError
  if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
    const axiosError = error as AxiosError
    // eslint-disable-next-line no-console
    console.log('Error Type: AxiosError')
    // eslint-disable-next-line no-console
    console.log('Error:', axiosError)
    // eslint-disable-next-line no-console
    console.log('Error Message:', axiosError.message)
    if (axiosError.response) {
      // eslint-disable-next-line no-console
      console.log('Response Status:', axiosError.response.status)
      // eslint-disable-next-line no-console
      console.log('Response Status Text:', axiosError.response.statusText)
      // eslint-disable-next-line no-console
      console.log('Response Data:', axiosError.response.data)
      if (
        axiosError.response.data &&
        typeof axiosError.response.data === 'object' &&
        'message' in axiosError.response.data
      ) {
        // eslint-disable-next-line no-console
        console.log('Error Message from Response:', (axiosError.response.data as { message: string }).message)
      }
    }
    if (axiosError.request) {
      // eslint-disable-next-line no-console
      console.log('Request:', axiosError.request)
    }
    if (axiosError.config) {
      // eslint-disable-next-line no-console
      console.log('Request Config:', {
        url: axiosError.config.url,
        method: axiosError.config.method,
        headers: redactAuthHeaders(axiosError.config.headers),
      })
    }
  } else if (error instanceof Error) {
    // eslint-disable-next-line no-console
    console.log('Error Type: Standard Error')
    // eslint-disable-next-line no-console
    console.log('Error:', error)
    // eslint-disable-next-line no-console
    console.log('Error Message:', error.message)
    // eslint-disable-next-line no-console
    console.log('Error Stack:', error.stack)
  } else {
    // eslint-disable-next-line no-console
    console.log('Error Type: Unknown')
    // eslint-disable-next-line no-console
    console.log('Error:', error)
  }

  // eslint-disable-next-line no-console
  console.log('API URL:', `${API_URL}${endpoint}`)
  // eslint-disable-next-line no-console
  console.log('Environment:', ENV_INFO.useProduction ? 'production' : 'development')
  // eslint-disable-next-line no-console
  console.log('Timestamp:', new Date().toISOString())
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/**
 * Check Railway API health and log results
 */
export const checkRailwayHealth = async (): Promise<{
  status: 'ok' | 'error' | 'timeout'
  response?: unknown
  error?: string
}> => {
  const healthUrl = `${API_URL.replace('/api/v1', '')}/health`

  logApiRequest('/health')

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    const response = await axios.get(healthUrl, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    clearTimeout(timeoutId)

    logApiResponse('/health', response, response.data)

    return {
      status: 'ok',
      response: response.data,
    }
  } catch (error) {
    const isCanceled = axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError')

    if (isCanceled) {
      logApiError('/health', error, 'Request timed out after 10 seconds')
      return {
        status: 'timeout',
        error: 'Request timed out after 10 seconds',
      }
    }

    logApiError('/health', error)
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}
